import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ArrowPathIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { AssetTagRow } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { ApiError } from '../gallery/api';
import { fetchAssetTagging, setAssetTags } from './tagging-api';
import { takePrefetchedTagging } from './tagging-prefetch';

/**
 * Panneau de tagging d'UNE photo (nouveau système « Taguer »). Charge TOUTE la taxonomie
 * de la bibliothèque fusionnée avec les liaisons de la photo (`GET …/tagging`), et présente
 * une CASE À COCHER par tag. Modèle BINAIRE : un tag est coché (validé) ou non — pas de
 * poids, pas de « rejet ».
 *
 * L'IA ne fait que PRÉ-REMPLIR : chaque tag proposé porte une BARRE DE CONFIANCE (longueur +
 * pourcentage écrit, jamais la couleur seule) mais AUCUN tag n'est coché d'office. Le panneau
 * fonctionne aussi SANS aucune donnée IA (tout décoché, aucune barre).
 *
 * Le commit (`POST …/tags/set`) est déclenché DE L'EXTÉRIEUR (bouton « Valider et suivant »
 * de `TagWalk`) via le handle impératif `commit` — l'issue renvoyée pilote l'avancement.
 * Un 403 (accès lecture seule) affiche un message inline dédié, non bloquant.
 */

export interface TagPanelHandle {
  /**
   * Commit le set des tags cochés (`POST …/tags/set`). Renvoie l'issue :
   * `'ok'` (avancer), `'forbidden'` (lecture seule), `'error'` (échec générique).
   */
  commit: () => Promise<'ok' | 'forbidden' | 'error'>;
}

interface TagPanelProps {
  assetId: string;
  /**
   * Appelé dès que le tagging de CETTE photo est prêt — `TagWalk` s'en sert pour lancer
   * le préchargement de la suivante (audit A5). Optionnel : le panneau reste utilisable
   * seul (tests, usages futurs hors marche séquentielle).
   */
  onReady?: () => void;
}

type Status = 'loading' | 'ready' | 'error';

/** Ramène une confiance IA [0..1] à un pourcentage entier borné [0..100]. */
function toPercent(confidence: number): number {
  return Math.round(Math.min(1, Math.max(0, confidence)) * 100);
}

export const TagPanel = forwardRef<TagPanelHandle, TagPanelProps>(function TagPanel(
  { assetId, onReady },
  ref,
): React.JSX.Element {
  const { t } = useI18n();
  const [status, setStatus] = useState<Status>('loading');
  const [tags, setTags] = useState<AssetTagRow[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<'readonly' | 'generic' | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // `onReady` lu via une ref : le rappel est une closure de `TagWalk`, recréée à chaque
  // rendu. En dépendance d'effet, il re-déclencherait le chargement en boucle.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Charge le tagging de la photo à chaque changement d'asset (annulable au démontage).
  // Le set coché initial reflète les liaisons DÉJÀ validées (`checked`) — l'IA n'y ajoute rien.
  //
  // La réponse a le plus souvent DÉJÀ été demandée par le préchargement de la photo
  // précédente (audit A5) : on la consomme alors au lieu de ré-émettre la requête, ce qui
  // supprime le « Chargement… » entre deux photos. Un rechargement EXPLICITE (bouton
  // « Réessayer », `reloadKey > 0`) court-circuite la réserve : l'utilisateur redemande
  // au serveur, pas une réponse déjà obtenue.
  useEffect(() => {
    const ctrl = new AbortController();
    setStatus('loading');
    setCommitError(null);
    const pending = reloadKey === 0 ? takePrefetchedTagging(assetId) : undefined;
    void (async (): Promise<void> => {
      try {
        const result = await (pending ?? fetchAssetTagging(assetId, ctrl.signal));
        if (ctrl.signal.aborted) return;
        setTags(result.tags);
        setChecked(new Set(result.tags.filter((tag) => tag.checked).map((tag) => tag.tagId)));
        setStatus('ready');
        onReadyRef.current?.();
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setStatus('error');
      }
    })();
    return () => ctrl.abort();
  }, [assetId, reloadKey]);

  const toggle = useCallback((tagId: string): void => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  }, []);

  const commit = useCallback(async (): Promise<'ok' | 'forbidden' | 'error'> => {
    if (status !== 'ready' || committing) return 'error';
    setCommitting(true);
    setCommitError(null);
    try {
      await setAssetTags(assetId, [...checked]);
      return 'ok';
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setCommitError('readonly');
        return 'forbidden';
      }
      setCommitError('generic');
      return 'error';
    } finally {
      setCommitting(false);
    }
  }, [assetId, checked, status, committing]);

  useImperativeHandle(ref, () => ({ commit }), [commit]);

  return (
    <div className="flex flex-col gap-3">
      {status === 'loading' ? (
        <p className="flex items-center gap-2 py-4 text-sm text-muted" role="status">
          <ArrowPathIcon
            className="h-5 w-5 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          {t('tagging.loading')}
        </p>
      ) : status === 'error' ? (
        <div className="flex flex-col items-start gap-2 py-4">
          <p className="flex items-start gap-2 text-sm text-danger">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{t('tagging.errorLoad')}</span>
          </p>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {t('tagging.retry')}
          </button>
        </div>
      ) : tags.length === 0 ? (
        <p className="py-4 text-sm text-muted">{t('tagging.empty')}</p>
      ) : (
        // Grille responsive : UNE colonne par défaut, DEUX dès `sm:` (cf. BulkTagModal).
        // Chaque entrée est un `<label>` enveloppant un checkbox `sr-only` (accessible).
        <ul className="grid grid-cols-1 gap-x-4 sm:grid-cols-2" aria-label={t('tagging.listLabel')}>
          {tags.map((tag) => {
            const isChecked = checked.has(tag.tagId);
            const pct = tag.confidence === null ? null : toPercent(tag.confidence);
            return (
              <li key={tag.tagId} className="py-0.5">
                <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 hover:bg-surface-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={committing}
                    onChange={() => toggle(tag.tagId)}
                    className="sr-only"
                  />
                  {/* Case visuelle : coche = validé (icône, jamais la couleur seule). */}
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      isChecked ? 'border-brand bg-brand text-brand-fg' : 'border-border bg-surface'
                    }`}
                  >
                    {isChecked && <CheckIcon className="h-4 w-4" />}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="truncate text-fg">{tag.label}</span>
                    {/* Barre de confiance IA : la LONGUEUR encode le score, avec le
                        pourcentage ÉCRIT à côté (jamais l'info par la couleur seule).
                        Absente si l'IA n'a rien suggéré (`confidence === null`). */}
                    {pct !== null && (
                      <span className="flex items-center gap-1.5">
                        <span
                          aria-hidden="true"
                          className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-2"
                        >
                          <span
                            className="block h-full rounded-full bg-brand"
                            style={{ width: `${pct}%` }}
                          />
                        </span>
                        <span className="shrink-0 tabular-nums text-xs text-muted">
                          {interpolate(t('tagging.confidence'), { pct })}
                        </span>
                      </span>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {commitError !== null && (
        <p role="alert" className="flex items-start gap-2 text-sm text-danger">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{commitError === 'readonly' ? t('tagging.readonly') : t('tagging.error')}</span>
        </p>
      )}
    </div>
  );
});
